import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const UNITS = [{ v: "flat", l: "Flat rate" }, { v: "per_sqft", l: "Per sq ft" }, { v: "hourly", l: "Hourly" }];

export default function Services() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", category: "General", unit_price: 0, unit: "flat", active: true });

  const load = () => api.listServices().then(setServices).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", category: "General", unit_price: 0, unit: "flat", active: true }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Service name required");
    const payload = { ...form, unit_price: Number(form.unit_price) || 0 };
    try {
      if (editing) await api.updateService(editing.id, payload); else await api.createService(payload);
      toast.success(`Service ${editing ? "updated" : "added"}`); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (s) => { if (!window.confirm(`Delete ${s.name}?`)) return; await api.deleteService(s.id); toast.success("Deleted"); load(); };

  const byCat = services.reduce((acc, s) => { (acc[s.category || "General"] ||= []).push(s); return acc; }, {});

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Service Catalog" subtitle="Your reusable services & pricing" actions={
        <Button onClick={openNew} className="bg-[#0B1726] hover:bg-slate-800" data-testid="add-service-button"><Plus size={18} className="mr-1" /> New Service</Button>
      } />

      {services.length === 0 && <div className="text-slate-400 bg-white border border-slate-200 rounded-lg p-10 text-center">No services yet. Build your catalog (e.g. House Wash, Driveway, Roof Wash).</div>}

      <div className="space-y-8">
        {Object.entries(byCat).map(([cat, list]) => (
          <div key={cat}>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{cat}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm group" data-testid={`service-card-${s.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-slate-900">{s.name}</div>
                      {s.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{s.description}</p>}
                    </div>
                    <div className="flex opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button onClick={() => openEdit(s)} className="text-slate-400 hover:text-[#00B4D8] p-1" data-testid={`edit-service-${s.id}`}><PencilSimple size={16} /></button>
                      <button onClick={() => remove(s)} className="text-slate-400 hover:text-rose-500 p-1"><Trash size={16} /></button>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-display font-extrabold text-xl text-[#00B4D8]">{money(s.unit_price)}</span>
                    <span className="text-xs text-slate-400">{UNITS.find((u) => u.v === s.unit)?.l}</span>
                  </div>
                  {!s.active && <span className="text-[10px] font-bold uppercase text-slate-400">Inactive</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "New"} Service</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="e.g. House Wash" data-testid="service-name-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Price</Label><Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="mt-1.5" data-testid="service-price-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pb-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="text-sm text-slate-600">Active</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0B1726] hover:bg-slate-800" data-testid="save-service-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
